import {PALETTES} from "./palettes";

/**
 * Dessin du fond « Voyageur » : chaque plan du paysage est peint UNE fois dans une image (pixel art, 1 pixel d'art = 1 pixel
 * du canvas), puis le bandeau ne fait plus que les faire glisser en CSS (voir VoyageurFond.jsx).
 * Adapté de l'artéfact « Aerith Parallax » de l'utilisateur (dessin original) : mêmes formes, mêmes graines, mêmes palettes.
 * Les images sont gardées en mémoire par ambiance : revenir sur le fond ne repeint rien.
 */

export const LARGEUR_PLAN = 512;    // un plan se répète tous les 512 pixels d'art (256 pour les herbes de premier plan)
export const HAUTEUR = 144;
export const SOL = 112;             // ligne du sol

/** Générateur pseudo-aléatoire à graine (mulberry32). */
function aleatoire(graine) {
    let a = graine >>> 0;
    return () => {
        a = (a + 0x6D2B79F5) >>> 0;
        let t = a;
        t = Math.imul(t ^ (t >>> 15), t | 1);
        t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
        return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
    };
}

function toile(largeur, hauteur) {
    const c = document.createElement("canvas");
    c.width = largeur;
    c.height = hauteur;
    const g = c.getContext("2d");
    g.imageSmoothingEnabled = false;
    return {c, g};
}

const rect = (g, couleur, x, y, l, h) => { g.fillStyle = couleur; g.fillRect(x, y, l, h); };

/* ---------- ciel (bandes de couleur tramées) et étoiles ---------- */

function peindreCiel(P) {
    const {c, g} = toile(LARGEUR_PLAN, HAUTEUR);
    const bande = 16;
    for (let y = 0; y < HAUTEUR; y++) {
        const b = Math.min(7, Math.floor(y / bande)), pos = y % bande;
        for (let x = 0; x < LARGEUR_PLAN; x++) {
            let col = P.ciel[b];
            if (b < 7 && pos >= bande - 2 && ((x + y) & 1)) col = P.ciel[b + 1];
            else if (b < 7 && pos === bande - 3 && x % 4 === 0 && y % 2 === 0) col = P.ciel[b + 1];
            rect(g, col, x, y, 1, 1);
        }
    }
    return c;
}

function peindreEtoiles(P) {
    const {c, g} = toile(LARGEUR_PLAN, HAUTEUR);
    if (P.etoiles <= 0) return c;
    const r = aleatoire(404);
    for (let i = 0; i < 140; i++) {
        const x = Math.floor(r() * LARGEUR_PLAN), y = Math.floor(r() * 84), k = r(), grosse = r() < 0.12;
        r();   // (la phase de scintillement de l'artéfact : le scintillement est ici celui du plan entier)
        if (!(k < P.etoiles && (P.etoiles >= 1 || y < 50))) continue;
        const col = grosse ? "#ffe9a8" : "#ffffff";
        rect(g, col, x, y, 1, 1);
        if (grosse) { rect(g, col, x - 1, y, 3, 1); rect(g, col, x, y - 1, 1, 3); }
    }
    return c;
}

/** Soleil ou lune, seul dans son image (posé une fois dans le bandeau, sans se répéter). */
function peindreAstre(P) {
    const r = P.orbR, cote = (r + 3) * 2 + 1;
    const {c, g} = toile(cote, cote);
    for (let dy = -r - 3; dy <= r + 3; dy++) {
        for (let dx = -r - 3; dx <= r + 3; dx++) {
            const d = Math.sqrt(dx * dx + dy * dy);
            if (d <= r) rect(g, P.astre, r + 3 + dx, r + 3 + dy, 1, 1);
            else if (d <= r + 3 && ((dx + dy) & 1) === 0) rect(g, P.halo, r + 3 + dx, r + 3 + dy, 1, 1);
        }
    }
    if (P.lune) {
        const o = r + 3;
        rect(g, "#cfcdaa", o - 3, o - 2, 2, 2);
        rect(g, "#cfcdaa", o + 1, o + 2, 3, 2);
        rect(g, "#cfcdaa", o + 2, o - 3, 1, 1);
    }
    return {image: c, cote};
}

/* ---------- nuages ---------- */

function nuage(g, cx, cy, s, A, B) {
    const boules = [[0, 0, 5], [7, -3, 7], [15, -1, 6], [22, 1, 5], [10, 2, 6]];
    const pris = new Set();
    boules.forEach(p => {
        const r = p[2] * s, R = Math.ceil(r);
        for (let y = -R; y <= R; y++) {
            for (let x = -R; x <= R; x++) {
                if (x * x + y * y <= r * r) {
                    const X = Math.round(cx + p[0] * s + x), Y = Math.round(cy + p[1] * s + y);
                    if (Y <= cy + 4 * s) pris.add(X + "," + Y);
                }
            }
        }
    });
    pris.forEach(k => {
        const [X, Y] = k.split(",").map(Number);
        rect(g, pris.has(X + "," + (Y + 2)) ? A : B, X, Y, 1, 1);
    });
}

function peindreNuages(P, nombre, yMin, yMax, sMin, sMax, graine) {
    const {c, g} = toile(LARGEUR_PLAN, HAUTEUR), r = aleatoire(graine);
    for (let i = 0; i < nombre; i++) {
        const cx = i * LARGEUR_PLAN / nombre + 20 + r() * 30, cy = yMin + r() * (yMax - yMin), s = sMin + r() * (sMax - sMin);
        [-LARGEUR_PLAN, 0, LARGEUR_PLAN].forEach(dec => nuage(g, cx + dec, cy, s, P.nuageA, P.nuageB));
    }
    return c;
}

/* ---------- montagnes, collines, forêts ---------- */

function peindreMontagnes(P) {
    const {c, g} = toile(LARGEUR_PLAN, HAUTEUR), sommet = [];
    for (let i = 0; i < LARGEUR_PLAN; i++) {
        const a = i / LARGEUR_PLAN * Math.PI * 2;
        const m = (1 - Math.abs(Math.sin(a * 3 + 0.6))) * 30 + (1 - Math.abs(Math.sin(a * 7 + 2))) * 10 + Math.sin(a * 2) * 6 + 18;
        sommet[i] = Math.round(SOL - 6 - m);
    }
    for (let i = 0; i < LARGEUR_PLAN; i++) {
        const pente = sommet[(i + 1) % LARGEUR_PLAN] - sommet[(i + LARGEUR_PLAN - 1) % LARGEUR_PLAN];
        const eclaire = pente < 0;
        const neige = Math.max(0, Math.round((60 - sommet[i]) * 0.3)) + ((i * 7) % 3 === 0 ? 1 : 0);
        for (let y = sommet[i]; y < SOL + 4; y++) {
            const d = y - sommet[i];
            let col;
            if (d < neige) col = eclaire ? P.neige : P.montL;
            else if (eclaire) col = d < 16 ? P.montL : P.mont;
            else col = d > 2 ? P.montD : P.mont;
            rect(g, col, i, y, 1, 1);
        }
    }
    return c;
}

function peindreCollines(base, a1, a2, a3, ph, col, colL) {
    const {c, g} = toile(LARGEUR_PLAN, HAUTEUR);
    for (let i = 0; i < LARGEUR_PLAN; i++) {
        const a = i / LARGEUR_PLAN * Math.PI * 2;
        const haut = Math.round(base - a1 * Math.sin(a * 2 + ph) - a2 * Math.sin(a * 5 + ph * 2) - a3 * Math.sin(a * 11 + ph * 3));
        rect(g, col, i, haut, 1, SOL + 6 - haut);
        rect(g, colL, i, haut, 1, 1);
        if (((i + haut) & 1) === 0) rect(g, colL, i, haut + 1, 1, 1);
    }
    return c;
}

function pin(g, cx, base, h, col, colL, tronc) {
    const haut = base - h, etages = Math.max(3, Math.round(h / 8)), hEtage = (h - 4) / etages, lMax = Math.round(h * 0.27) + 2;
    for (let y = haut; y < base - 4; y++) {
        const d = y - haut, k = Math.min(etages - 1, Math.floor(d / hEtage)), r = (d - k * hEtage) / hEtage;
        const demi = Math.max(0, Math.round((k + 0.25 + r * 0.85) / (etages + 0.1) * lMax));
        if (r > 0.8) rect(g, col, cx - demi, y, demi * 2 + 1, 1);
        else { rect(g, colL, cx - demi, y, demi, 1); rect(g, col, cx, y, demi + 1, 1); }
    }
    rect(g, tronc, cx - 1, base - 4, 2, 4);
}

function peindreForet(P, graine, espacement, hMin, hMax, col, colL) {
    const {c, g} = toile(LARGEUR_PLAN, HAUTEUR), r = aleatoire(graine), n = Math.floor(LARGEUR_PLAN / espacement);
    for (let k = 0; k < n; k++) {
        const cx = Math.round(k * espacement + espacement / 2 + (r() - 0.5) * espacement * 0.6);
        const h = Math.round(hMin + r() * (hMax - hMin));
        [-LARGEUR_PLAN, 0, LARGEUR_PLAN].forEach(dec => pin(g, cx + dec, SOL + 2, h, col, colL, P.tronc));
    }
    return c;
}

/* ---------- sol et décor ---------- */

function buisson(g, P, cx) {
    const cs = [[-8, -3, 5], [-2, -6, 7], [6, -4, 6], [11, -1, 4]];
    cs.forEach(([dx, dy, r]) => {
        for (let y = -r; y <= r; y++) {
            for (let x = -r; x <= r; x++) {
                if (x * x + y * y > r * r) continue;
                const X = cx + dx + x, Y = SOL + dy + y;
                if (Y > SOL + 1) continue;
                let col = (x + y) < -r * 0.3 ? P.buissonL : P.buisson;
                if (Y >= SOL - 1) col = P.herbeD;
                rect(g, col, X, Y, 1, 1);
            }
        }
    });
    rect(g, P.fleur1, cx - 6, SOL - 8, 1, 1);
    rect(g, P.fleur1, cx + 3, SOL - 10, 1, 1);
    rect(g, P.fleur2, cx + 9, SOL - 5, 1, 1);
}

function roche(g, P, cx) {
    const rx = 7, ry = 5;
    for (let y = -ry; y <= ry; y++) {
        for (let x = -rx; x <= rx; x++) {
            if ((x * x) / (rx * rx) + (y * y) / (ry * ry) > 1) continue;
            const Y = SOL - 2 + y;
            if (Y > SOL + 1) continue;
            rect(g, (x + y) < -2 ? P.rocheL : P.roche, cx + x, Y, 1, 1);
        }
    }
    rect(g, P.terreD, cx + 1, SOL - 4, 1, 2);
}

function fleurs(g, P, cx, graine) {
    const r = aleatoire(graine);
    for (let i = 0; i < 4; i++) {
        const fx = cx + i * 5 + Math.floor(r() * 3), h = 3 + Math.floor(r() * 3), fy = SOL - h;
        rect(g, P.herbeD, fx, fy + 1, 1, h);
        const col = (i & 1) ? P.fleur2 : P.fleur1;
        rect(g, col, fx, fy - 1, 1, 1); rect(g, col, fx - 1, fy, 1, 1); rect(g, col, fx + 1, fy, 1, 1); rect(g, col, fx, fy + 1, 1, 1);
        rect(g, "#fff6d0", fx, fy, 1, 1);
    }
}

function panneau(g, P, cx) {
    rect(g, P.terreD, cx, SOL - 16, 2, 17);
    rect(g, P.panneau, cx - 9, SOL - 18, 19, 7);
    rect(g, P.panneau, cx + 10, SOL - 17, 1, 5);
    rect(g, P.panneau, cx + 11, SOL - 16, 1, 3);
    rect(g, P.panneau, cx + 12, SOL - 15, 1, 1);
    for (let i = 0; i < 8; i++) {
        if (i % 2 === 0) rect(g, P.terreD, cx - 7 + i * 2, SOL - 16, 1, 1);
        if (i % 3 !== 1) rect(g, P.terreD, cx - 7 + i * 2, SOL - 14, 1, 1);
    }
}

function peindreSol(P) {
    const {c, g} = toile(LARGEUR_PLAN, HAUTEUR), r = aleatoire(77), y0 = SOL + 4;
    for (let tr = 0; y0 + tr * 8 < HAUTEUR; tr++) {
        for (let tx = 0; tx < LARGEUR_PLAN / 8; tx++) {
            const yy = y0 + tr * 8;
            rect(g, ((tx + tr) & 1) ? P.terreA : P.terreB, tx * 8, yy, 8, Math.min(8, HAUTEUR - yy));
        }
    }
    rect(g, P.herbeL, 0, SOL, LARGEUR_PLAN, 1);
    rect(g, P.herbe, 0, SOL + 1, LARGEUR_PLAN, 1);
    rect(g, P.herbeD, 0, SOL + 2, LARGEUR_PLAN, 1);
    rect(g, P.terreD, 0, SOL + 3, LARGEUR_PLAN, 1);
    for (let x = 0; x < LARGEUR_PLAN; x++) {
        if (x % 4 < 2) rect(g, P.herbe, x, SOL + 2, 1, 1);
        if ((x * 3) % 8 < 2) rect(g, P.herbeD, x, SOL + 3, 1, 1);
    }
    for (let i = 0; i < 80; i++) {
        const tx = Math.floor(r() * LARGEUR_PLAN);
        rect(g, P.herbeL, tx, SOL - 2, 1, 2);
        if (r() < 0.5) rect(g, P.herbe, tx + 1, SOL - 1, 1, 1);
    }
    for (let i = 0; i < 60; i++) rect(g, P.caillou, Math.floor(r() * LARGEUR_PLAN), SOL + 7 + Math.floor(r() * (HAUTEUR - SOL - 9)), 2, 1);
    for (let i = 0; i < 50; i++) rect(g, P.terreD, Math.floor(r() * LARGEUR_PLAN), SOL + 6 + Math.floor(r() * (HAUTEUR - SOL - 8)), 1, 1);
    [-LARGEUR_PLAN, 0, LARGEUR_PLAN].forEach(dec => {
        [40, 185, 345, 470].forEach(x => buisson(g, P, x + dec));
        [120, 300, 410].forEach(x => roche(g, P, x + dec));
        [75, 150, 225, 380, 440, 500].forEach((x, idx) => fleurs(g, P, x + dec, 100 + idx));
        panneau(g, P, 250 + dec);
    });
    return c;
}

function peindreHerbes(P) {
    const l = LARGEUR_PLAN / 2, {c, g} = toile(l, HAUTEUR), r = aleatoire(5);
    for (let x = 0; x < l; x++) {
        if (r() >= 0.55) continue;
        let h = 3 + Math.floor(r() * 6);
        if (r() < 0.08) h += 7;
        const penche = r() < 0.5 ? -1 : 1;
        for (let k = 0; k < h; k++) {
            const xx = (x + (k > h * 0.6 ? penche : 0) + l) % l;
            rect(g, k > h - 3 ? P.avantL : P.avant, xx, HAUTEUR - 1 - k, 1, 1);
        }
    }
    rect(g, P.avant, 0, HAUTEUR - 2, l, 2);
    return c;
}

/* ---------- le voyageur : 8 images d'un cycle de pas, côte à côte ---------- */

const CORPS = [
    ".....oooooo.....",
    "....occCCcco....",
    "...occCCcccco...",
    "..occCcccccco...",
    "..occcccssssso..",
    "..occccsseesso..",
    "..occccssssssoo.",
    "...occcossssso..",
    "...orrrrrrrro...",
    "..ootttttttoo...",
    "..otttTtttttto..",
    "..obbbbBbbbbo...",
];
const COULEURS = {o: "#1a1030", c: "#2fa39a", C: "#6fe0c8", s: "#f6c9a4", e: "#1a1030", r: "#ff6b4a", t: "#4a5fd1", T: "#7389f0", b: "#8a5a2b", B: "#ffd35a"};

export const IMAGES_PAR_CYCLE = 8;
/** Cadre d'une image du voyageur : 32 × 24 pixels d'art ; le coin haut gauche du corps est en (DECAL_X, DECAL_Y). */
export const CADRE = {largeur: 32, hauteur: 24, decalX: 10, decalY: 2, centre: 10};
/** Case d'une feuille personnalisée (outil Sprites) : 64 × 48, centre du personnage à 32, pieds sur la ligne 40. */
export const CADRE_FEUILLE = {largeur: 64, hauteur: 48, centre: 32, pieds: 40};

function peindreVoyageur(P, allure) {
    const marche = allure === "marcher";
    const {c, g} = toile(CADRE.largeur * IMAGES_PAR_CYCLE, CADRE.hauteur);
    const A = marche ? 3 : 5, LF = marche ? 2 : 4, AS = marche ? 2 : 3, BA = marche ? 0.6 : 1.2;

    for (let image = 0; image < IMAGES_PAR_CYCLE; image++) {
        const q = image / IMAGES_PAR_CYCLE;
        const ox = image * CADRE.largeur + CADRE.decalX, oy = CADRE.decalY;
        const R = (x, y, l, h, col) => rect(g, col, ox + x, oy + y, l, h);
        const L = (x0, y0, x1, y1, col) => {
            const dx = Math.abs(x1 - x0), dy = Math.abs(y1 - y0), sx = x0 < x1 ? 1 : -1, sy = y0 < y1 ? 1 : -1;
            let err = dx - dy;
            for (;;) {
                R(x0, y0, 2, 1, col);
                if (x0 === x1 && y0 === y1) break;
                const e2 = 2 * err;
                if (e2 > -dy) { err -= dy; x0 += sx; }
                if (e2 < dx) { err += dx; y0 += sy; }
            }
        };
        const bob = -Math.round(Math.abs(Math.sin(q * Math.PI * 2)) * BA);
        const ad = Math.round(Math.cos(q * Math.PI * 2) * AS);

        // ombre au sol
        g.fillStyle = "rgba(0,0,0,0.30)";
        g.fillRect(ox + 3, oy + 20, 11, 1);
        g.fillRect(ox + 5, oy + 21, 7, 1);

        // bras arrière
        L(8, 10 + bob, 8 + ad, 14 + bob, "#33449a");
        R(8 + ad, 14 + bob, 2, 2, COULEURS.s);

        // jambes
        const jambe = (ph, couleur, botte) => {
            const dx = Math.round(Math.cos(ph * 2 * Math.PI) * A);
            const leve = ph > 0.5 ? Math.round(Math.sin((ph - 0.5) * 2 * Math.PI) * LF) : 0;
            const fx = 8 + dx, fy = 18 - leve;
            L(7, 13 + bob, fx, fy, couleur);
            R(fx - 1, fy, 4, 2, botte);
        };
        jambe((q + 0.5) % 1, "#2a1d47", "#4e2a14");
        jambe(q, "#3f2d68", "#6b3a1e");

        // bassin, corps, tête
        R(6, 12 + bob, 5, 2, "#3f2d68");
        CORPS.forEach((ligne, j) => {
            for (let i = 0; i < ligne.length; i++) {
                if (ligne[i] !== ".") R(i, j + bob, 1, 1, COULEURS[ligne[i]]);
            }
        });

        // écharpe qui flotte : une onde complète par cycle (le cycle se répète sans à-coup)
        for (let i = 1; i <= 10; i++) {
            const onde = Math.sin(q * Math.PI * 2 - i * 0.75) * (0.6 + i * 0.18);
            R(3 - i, 8 + bob + Math.round(onde) + (i > 5 ? 1 : 0), 1, i < 8 ? 2 : 1, i % 3 === 0 ? "#ff9a78" : "#ff6b4a");
        }

        // bras avant
        L(8, 10 + bob, 8 - ad, 14 + bob, COULEURS.T);
        R(8 - ad, 14 + bob, 2, 2, COULEURS.s);
    }
    return c;
}

/* ---------- l'ensemble, par ambiance ---------- */

const memoire = {};

/** Image (adresse « data: ») d'une toile. */
const adresse = c => c.toDataURL("image/png");

/**
 * Toutes les images d'une ambiance (« jour », « crepuscule » ou « nuit »), peintes une fois puis gardées :
 * {ciel, etoiles, astre: {url, cote, y}, plans: {identifiant du plan: url}, voyageur: {courir: url, marcher: url}}.
 */
export function imagesAmbiance(ambiance) {
    if (memoire[ambiance]) return memoire[ambiance];
    const P = PALETTES[ambiance] || PALETTES.crepuscule;
    const astre = peindreAstre(P);
    memoire[ambiance] = {
        ciel: adresse(peindreCiel(P)),
        etoiles: P.etoiles > 0 ? adresse(peindreEtoiles(P)) : null,
        astre: {url: adresse(astre.image), cote: astre.cote, y: P.orbY - (P.orbR + 3)},
        plans: {
            nuages1: adresse(peindreNuages(P, 5, 36, 58, 0.7, 1.0, 11)),
            montagnes: adresse(peindreMontagnes(P)),
            nuages2: adresse(peindreNuages(P, 4, 58, 78, 1.0, 1.5, 23)),
            collinesA: adresse(peindreCollines(80, 12, 6, 2, 0.8, P.collA, P.collAL)),
            collinesB: adresse(peindreCollines(93, 9, 5, 2, 2.1, P.collB, P.collBL)),
            foretArriere: adresse(peindreForet(P, 31, 14, 18, 28, P.foretA, P.foretAL)),
            foretAvant: adresse(peindreForet(P, 57, 20, 28, 44, P.foretB, P.foretBL)),
            sol: adresse(peindreSol(P)),
            herbes: adresse(peindreHerbes(P)),
        },
        voyageur: {courir: adresse(peindreVoyageur(P, "courir")), marcher: adresse(peindreVoyageur(P, "marcher"))},
    };
    return memoire[ambiance];
}
